/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
import SelectMenuItem from 'components/ChoreoSystem/SimpleSelect/SelectMenuItem';
import SimpleSelect from 'components/ChoreoSystem/SimpleSelect/SimpleSelect';
import { REGION_EU, REGION_US } from 'modules/project/CreateProject/constant';
import logger from 'utils/logger';
import useStyles from './styles';
import { useRegionSelection } from './utils';

interface RegionSelectorProps {
  availableRegions: string[];
  showRegionLabel?: boolean;
}

export default function RegionSelector(props: RegionSelectorProps) {
  const classes = useStyles();
  const { availableRegions, showRegionLabel } = props;

  const { regionDomainMap, selectedRegion } =
    useRegionSelection(availableRegions);

  const getRegionLabel = (region: string) => {
    let flagIconUrl = '';
    switch (region) {
      case REGION_US:
        flagIconUrl = '/images/us.svg';
        break;
      case REGION_EU:
        flagIconUrl = '/images/eu.svg';
        break;
      default:
        flagIconUrl = '';
        break;
    }

    return (
      <Box display="flex" gridGap={8}>
        {flagIconUrl && (
          <img
            src={flagIconUrl}
            alt={`${region} flag`}
            className={classes.flagIcon}
          />
        )}
        <Typography variant="body1" className={classes.regionLabel}>
          {region}
        </Typography>
      </Box>
    );
  };

  const handleRegionChange = (
    e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>
  ) => {
    const region = e.target.value as string;
    const currentUrl = new URL(window.location.href);
    const domain = regionDomainMap.get(region);
    if (domain) {
      const isInIframe = window.self !== window.top;
      const redirectPath = `${domain}/${
        isInIframe ? 'signup_embedded' : 'login'
      }`;
      const newUrl = new URL(redirectPath);

      // Detect VSCode/CLI login
      const isVSCode = currentUrl.searchParams.get('profile') === 'vs-code';
      let updatedClientId: string | undefined;

      // Handle state parameter if it exists
      const stateParam = currentUrl.searchParams.get('state');
      if (stateParam) {
        try {
          const decodedState = atob(stateParam);
          const stateObj = JSON.parse(decodedState);

          // Find correct clientId for the selected region
          if (Array.isArray(stateObj.regions)) {
            const regionEntry = stateObj.regions.find(
              (r: any) => r.region === region
            );
            if (regionEntry && regionEntry.clientId) {
              updatedClientId = regionEntry.clientId;
            }
          }

          // Add/update region in state
          stateObj.region = region;
          const updatedState = btoa(JSON.stringify(stateObj));
          newUrl.searchParams.set('state', updatedState);
        } catch (error) {
          newUrl.searchParams.set('state', stateParam);
          logger.error(error);
        }
      }

      // Copy all other params except client_id and state
      currentUrl.searchParams.forEach((value, key) => {
        if (key !== 'state' && key !== 'client_id') {
          newUrl.searchParams.set(key, value);
        }
      });

      // Set correct client_id if VSCode/CLI login
      if (isVSCode && updatedClientId) {
        newUrl.searchParams.set('client_id', updatedClientId);
      }

      window.location.href = newUrl.toString();
    }
  };

  return (
    <Box
      className={clsx(classes.root, {
        [classes.rootEmbedded]: showRegionLabel,
      })}
    >
      {showRegionLabel && (
        <Typography variant="body1" noWrap>
          <FormattedMessage
            id="modules.login.RegionSelector.label"
            defaultMessage="Your Region:"
          />
        </Typography>
      )}
      <Box className={classes.selectorRoot}>
        <SimpleSelect
          value={selectedRegion}
          onChange={handleRegionChange}
          variant="outlined"
          size="small"
          testId="region-selector"
          aria-label="Region"
        >
          {Array.from(regionDomainMap.keys()).map((region) => (
            <SelectMenuItem
              key={region}
              value={region}
              testId={`region-selector-${region}`}
              className={classes.menuItem}
            >
              {getRegionLabel(region)}
            </SelectMenuItem>
          ))}
        </SimpleSelect>
      </Box>
    </Box>
  );
}
